import { isEmpty } from 'lodash';
import React, { useMemo } from 'react';
import { LuBuilding, LuLock } from 'react-icons/lu';

import { TradeRestrictionAsset } from '@ndvr/portfolio-api';

import { ImageWithFallbackIcon } from '@/components/ImageWithFallbackIcon';
import { SimpleSortableHeader } from '@/components/SimpleSortableHeader';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSimpleTableSort } from '@/hooks/useSimpleTableSort';
import { formatCurrencyHuman, formatPercent } from 'utils';

import { type AffectedStock } from './AffectedStocksTable';

type SortColumn = 'ticker' | 'marketValue' | 'status';

interface ExcludedStocksTabProps {
    affectedStocks: AffectedStock[];
    restrictedStockIds: string[];
    restrictions: TradeRestrictionAsset[];
    onExclusionChange: (stockId: string, excluded: boolean) => void;
}

export const ExcludedStocksTab = ({
    affectedStocks,
    restrictedStockIds,
    restrictions,
    onExclusionChange,
}: ExcludedStocksTabProps) => {
    const handleStockToggle = (stockId: string, excluded: boolean) => {
        onExclusionChange(stockId, excluded);
    };

    const isStockClientRestricted = (stock: AffectedStock) => {
        return restrictions.some((restriction) => restriction.shareId === stock.shareId);
    };

    const getStockStatus = (stock: AffectedStock): number => {
        if (isStockClientRestricted(stock)) return 2; // Client restricted (last)
        if (restrictedStockIds.includes(stock.shareClassId)) return 1; // Restricted
        return 0; // Tradable (first)
    };

    const sortFunctions = useMemo(
        () => ({
            ticker: (a: AffectedStock, b: AffectedStock) => a.ticker.localeCompare(b.ticker),
            marketValue: (a: AffectedStock, b: AffectedStock) => a.marketValue - b.marketValue,
            status: (a: AffectedStock, b: AffectedStock) => getStockStatus(a) - getStockStatus(b),
        }),
        [restrictedStockIds, restrictions],
    );

    const { sortedData, sortColumn, sortDirection, handleSort } = useSimpleTableSort<AffectedStock, SortColumn>({
        data: affectedStocks,
        sortFunctions,
        initialSort: {
            column: 'marketValue',
            direction: 'desc',
        },
    });

    return (
        <div className="flex h-full flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto rounded border">
                <Table>
                    <TableHeader className="sticky top-0 bg-gray-50">
                        <TableRow>
                            <SimpleSortableHeader
                                column="ticker"
                                label="Stock"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SimpleSortableHeader
                                column="marketValue"
                                label="Market Cap"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                className="text-center"
                            />
                            <SimpleSortableHeader
                                column="status"
                                label="Trade Status"
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                className="w-48 text-center"
                            />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {sortedData.map((stock) => {
                            const isClientRestricted = isStockClientRestricted(stock);
                            return (
                                <TableRow key={stock.shareClassId}>
                                    <TableCell>
                                        <div className="flex items-center gap-3">
                                            <ImageWithFallbackIcon
                                                src={stock.logoUrl}
                                                alt={stock.ticker}
                                                fallbackIcon={LuBuilding}
                                                className="h-6 w-6 rounded object-cover"
                                            />
                                            <div>
                                                <div className="text-cocoa-1000 font-medium">{stock.ticker}</div>
                                                <div className="text-sm text-gray-500">{stock.name}</div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <div className="text-cocoa-1000 text-sm">
                                            {formatCurrencyHuman(stock.marketValue, 'USD')}
                                        </div>
                                        <div className="text-sm text-gray-500">{formatPercent(stock.percentage)}</div>
                                    </TableCell>
                                    <TableCell className="w-48 text-center">
                                        {isClientRestricted ? (
                                            <div className="flex items-center justify-center gap-2">
                                                <span className="text-sm text-gray-400">Client Restricted</span>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="inline-flex">
                                                            <LuLock className="h-4 w-4 text-gray-500" />
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="bottom" align="center" sideOffset={5}>
                                                        <p>You already added this as a client restriction</p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        ) : (
                                            <ToggleGroup
                                                type="single"
                                                value={
                                                    restrictedStockIds.includes(stock.shareClassId)
                                                        ? 'restricted'
                                                        : 'tradable'
                                                }
                                                onValueChange={(value) => {
                                                    if (!isEmpty(value)) {
                                                        handleStockToggle(stock.shareClassId, value === 'restricted');
                                                    }
                                                }}
                                                variant="outline"
                                                size="sm"
                                                className="w-fit"
                                            >
                                                <ToggleGroupItem
                                                    value="tradable"
                                                    className="data-[state=on]:bg-accent/20 cursor-pointer font-normal hover:bg-transparent hover:text-current data-[state=on]:border-1 data-[state=on]:border-l data-[state=on]:border-gray-400 data-[state=on]:font-medium data-[state=on]:text-green-800"
                                                >
                                                    Tradable
                                                </ToggleGroupItem>
                                                <ToggleGroupItem
                                                    value="restricted"
                                                    className="data-[state=on]:bg-accent/20 cursor-pointer pr-2 pl-2 font-normal hover:bg-transparent hover:text-current data-[state=on]:border-1 data-[state=on]:!border-l-[1px] data-[state=on]:border-gray-400 data-[state=on]:font-medium data-[state=on]:text-red-900"
                                                >
                                                    Restricted
                                                </ToggleGroupItem>
                                            </ToggleGroup>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>

                {sortedData.length === 0 && (
                    <div className="py-8 text-center text-sm text-gray-500">No affected stocks found</div>
                )}
            </div>
        </div>
    );
};
